/** Fallback locale for call sites with no live locale to hand in (tests, pure-data helpers). */
export const DEFAULT_NUMBER_LOCALE = 'en-US'

const compactFormatterCache = new Map<string, Intl.NumberFormat>()
const decimalFormatterCache = new Map<string, Intl.NumberFormat>()
const fixedFormatterCache = new Map<string, Intl.NumberFormat>()

function compactFormatterFor(locale: string): Intl.NumberFormat {
  let formatter = compactFormatterCache.get(locale)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { notation: 'compact' })
    compactFormatterCache.set(locale, formatter)
  }
  return formatter
}

/** Renders a count in compact notation keeping two significant digits (`1.2K`, `1.3M`); `''` for `null`/`undefined` so absence differs from zero. */
export function formatCompactNumber(
  value: number | null | undefined,
  locale: string = DEFAULT_NUMBER_LOCALE,
): string {
  if (value === null || value === undefined) return ''
  return compactFormatterFor(locale).format(value)
}

function decimalFormatterFor(locale: string, maximumFractionDigits: number): Intl.NumberFormat {
  const key = `${locale}:${maximumFractionDigits}`
  let formatter = decimalFormatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, { maximumFractionDigits })
    decimalFormatterCache.set(key, formatter)
  }
  return formatter
}

/** Renders a number in full digits with at most `maximumFractionDigits` decimals, in `locale`. */
export function formatDecimal(
  value: number,
  locale: string = DEFAULT_NUMBER_LOCALE,
  maximumFractionDigits = 1,
): string {
  return decimalFormatterFor(locale, maximumFractionDigits).format(value)
}

/** Renders a number with exactly `fractionDigits` decimals in `locale`, so a column of averages lines up. */
export function formatFixed(
  value: number,
  locale: string = DEFAULT_NUMBER_LOCALE,
  fractionDigits = 1,
): string {
  const key = `${locale}:${fractionDigits}`
  let formatter = fixedFormatterCache.get(key)
  if (!formatter) {
    formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    })
    fixedFormatterCache.set(key, formatter)
  }
  return formatter.format(value)
}
